import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, Shield, Warehouse, DollarSign, Filter, ChevronDown, ArrowUpDown, CalendarClock } from 'lucide-react';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_FILTERS = [
    { value: '', label: 'Toutes les sections' },
    { value: 'rdv_non_affectes', label: 'RDV non affectés' },
    { value: 'aot', label: 'AOT' },
    { value: 'monte_meuble', label: 'Monte-meuble' },
    { value: 'garde_meuble', label: 'Garde-meuble' },
    { value: 'acompte', label: 'Acompte' },
];

export default function Urgent() {
    const navigate = useNavigate();
    const [aotUrgent, setAotUrgent] = useState([]);
    const [gmUrgent, setGmUrgent] = useState([]);
    const [mmUrgent, setMmUrgent] = useState([]);
    const [acompteUrgent, setAcompteUrgent] = useState([]);
    const [rdvUrgent, setRdvUrgent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sectionFilter, setSectionFilter] = useState('');

    useEffect(() => {
        const fetch = async () => {
            try {
                const [aotRes, gmRes, mmRes, clientsRes, rdvRes] = await Promise.all([
                    axios.get(`${API_URL}/api/aot-list`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/garde-meuble-list`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/monte-meuble-list`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/clients`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/dashboard/rdv-non-affectes`, { withCredentials: true }).catch(() => ({ data: [] })),
                ]);
                setAotUrgent(aotRes.data.filter(i => i.is_urgent));
                setMmUrgent(mmRes.data.filter(i => i.is_urgent));
                const now = new Date();
                const urgentGm = gmRes.data.filter(i => {
                    if (i.date_entree && i.statut !== 'entree_reservee' && i.statut !== 'sortie_planifiee') {
                        const d = new Date(i.date_entree + 'T00:00');
                        if (Math.ceil((d - now) / (1000 * 60 * 60 * 24)) <= 7) return true;
                    }
                    if (i.date_sortie && i.statut !== 'sortie_planifiee') {
                        const d = new Date(i.date_sortie + 'T00:00');
                        if (Math.ceil((d - now) / (1000 * 60 * 60 * 24)) <= 7) return true;
                    }
                    return false;
                });
                setGmUrgent(urgentGm);
                setAcompteUrgent(clientsRes.data.filter(c => c.acompte_urgent));
                setRdvUrgent(rdvRes.data || []);
            } catch {
                toast.error('Erreur');
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, []);

    const totalUrgent = aotUrgent.length + gmUrgent.length + mmUrgent.length + acompteUrgent.length + rdvUrgent.length;

    return (
        <Layout>
            <div className="space-y-6" data-testid="urgent-page">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-500 hover:text-[#1B365D] transition-colors text-sm" data-testid="urgent-back-btn">
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Urgent
                    </h1>
                    <Badge className="bg-red-500 text-white text-xs px-2">{totalUrgent}</Badge>
                    <div className="ml-auto relative min-w-[200px]">
                        <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <select value={sectionFilter} onChange={e => setSectionFilter(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 appearance-none"
                            data-testid="urgent-section-filter">
                            {SECTION_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B365D]" />
                    </div>
                ) : totalUrgent === 0 ? (
                    <Card className="shadow-sm">
                        <CardContent className="py-12">
                            <div className="flex flex-col items-center justify-center text-center">
                                <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                                    <AlertTriangle className="h-8 w-8 text-gray-400" />
                                </div>
                                <p className="text-gray-500 font-medium">Aucun element urgent</p>
                                <p className="text-gray-400 text-sm mt-1">Les elements urgents apparaitront ici automatiquement</p>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-6">
                        {/* RDV non affectés */}
                        {(!sectionFilter || sectionFilter === 'rdv_non_affectes') && rdvUrgent.length > 0 && (
                            <Card className="shadow-sm border-l-4 border-l-red-400">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base font-semibold text-[#1B365D] flex items-center gap-2">
                                        <CalendarClock className="w-4 h-4 text-red-500" />
                                        RDV non affectés - saisis il y a moins d'1h ({rdvUrgent.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {rdvUrgent.map((item, i) => (
                                            <div key={i}
                                                onClick={() => navigate(`/agenda/${item.id}`)}
                                                className="flex items-center justify-between p-3 rounded-xl border border-red-100 hover:bg-red-50/30 cursor-pointer transition-all group">
                                                <div className="flex items-center gap-3">
                                                    <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 font-bold">NON AFFECTÉ</Badge>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-800 group-hover:text-[#1B365D]">{item.nom || 'Sans nom'}</p>
                                                        <p className="text-xs text-gray-400">{item.telephone || '-'}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {item.type && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{item.type}</Badge>}
                                                    <span className="text-xs text-gray-500">
                                                        {item.date ? new Date(item.date + 'T00:00').toLocaleDateString('fr-FR') : ''}
                                                        {item.time ? ` · ${item.time}` : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* AOT Urgent */}
                        {(!sectionFilter || sectionFilter === 'aot') && aotUrgent.length > 0 && (
                            <Card className="shadow-sm border-l-4 border-l-red-400">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base font-semibold text-[#1B365D] flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-red-500" />
                                        AOT urgentes ({aotUrgent.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {aotUrgent.map((item, i) => (
                                            <div key={i} onClick={() => navigate(`/clients/${item.client_id}`)}
                                                className="flex items-center justify-between p-3 rounded-xl border border-red-100 hover:bg-red-50/30 cursor-pointer transition-all group">
                                                <div className="flex items-center gap-3">
                                                    <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 font-bold">URGENT</Badge>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-800 group-hover:text-[#1B365D]">{item.client_nom}</p>
                                                        <p className="text-xs text-gray-400">{item.adresse || '-'}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {(() => {
                                                        const dir = item.direction || item.type;
                                                        const isDepart = dir === 'depart';
                                                        return (
                                                            <Badge className={`text-[10px] ${isDepart ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                                                                {isDepart ? 'Partir' : 'Arriver'}
                                                            </Badge>
                                                        );
                                                    })()}
                                                    <span className="text-xs text-gray-400">{item.date ? new Date(item.date + 'T00:00').toLocaleDateString('fr-FR') : ''}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Monte-meuble Urgent */}
                        {(!sectionFilter || sectionFilter === 'monte_meuble') && mmUrgent.length > 0 && (
                            <Card className="shadow-sm border-l-4 border-l-red-400">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base font-semibold text-[#1B365D] flex items-center gap-2">
                                        <ArrowUpDown className="w-4 h-4 text-red-500" />
                                        Monte-meuble urgent ({mmUrgent.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {mmUrgent.map((item, i) => (
                                            <div key={i} onClick={() => navigate(`/clients/${item.client_id}`)}
                                                className="flex items-center justify-between p-3 rounded-xl border border-red-100 hover:bg-red-50/30 cursor-pointer transition-all group">
                                                <div className="flex items-center gap-3">
                                                    <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 font-bold">URGENT</Badge>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-800 group-hover:text-[#1B365D]">{item.client_nom}</p>
                                                        <p className="text-xs text-gray-400">{item.adresse || '-'}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge className={`text-[10px] ${item.type === 'depart' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                                                        {item.type === 'depart' ? 'Partir' : 'Arriver'}
                                                    </Badge>
                                                    <span className="text-xs text-gray-400">{item.date ? new Date(item.date + 'T00:00').toLocaleDateString('fr-FR') : ''}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Garde-meuble Urgent */}
                        {(!sectionFilter || sectionFilter === 'garde_meuble') && gmUrgent.length > 0 && (
                            <Card className="shadow-sm border-l-4 border-l-red-400">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base font-semibold text-[#1B365D] flex items-center gap-2">
                                        <Warehouse className="w-4 h-4 text-red-500" />
                                        Garde-meuble urgent ({gmUrgent.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {gmUrgent.map((item, i) => (
                                            <div key={i} onClick={() => navigate(`/clients/${item.client_id}`)}
                                                className="flex items-center justify-between p-3 rounded-xl border border-red-100 hover:bg-red-50/30 cursor-pointer transition-all group">
                                                <div className="flex items-center gap-3">
                                                    <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 font-bold">URGENT</Badge>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-800 group-hover:text-[#1B365D]">{item.client_nom}</p>
                                                        <p className="text-xs text-gray-400">
                                                            {item.date_entree ? `Entree: ${new Date(item.date_entree + 'T00:00').toLocaleDateString('fr-FR')}` : ''}
                                                            {item.date_sortie ? ` | Sortie: ${new Date(item.date_sortie + 'T00:00').toLocaleDateString('fr-FR')}` : ''}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Badge className="bg-amber-100 text-amber-700 text-[10px]">{item.statut === 'en_attente' ? 'En attente' : item.statut}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Acompte Urgent */}
                        {(!sectionFilter || sectionFilter === 'acompte') && acompteUrgent.length > 0 && (
                            <Card className="shadow-sm border-l-4 border-l-red-400">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base font-semibold text-[#1B365D] flex items-center gap-2">
                                        <DollarSign className="w-4 h-4 text-red-500" />
                                        Acompte non verse - urgent ({acompteUrgent.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {acompteUrgent.map((item, i) => (
                                            <div key={i} onClick={() => navigate(`/clients/${item.id}`)}
                                                className="flex items-center justify-between p-3 rounded-xl border border-red-100 hover:bg-red-50/30 cursor-pointer transition-all group">
                                                <div className="flex items-center gap-3">
                                                    <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 font-bold">URGENT</Badge>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-800 group-hover:text-[#1B365D]">{item.nom}</p>
                                                        <p className="text-xs text-gray-400">{item.date_demenagement ? `Dem: ${new Date(item.date_demenagement + 'T00:00').toLocaleDateString('fr-FR')}` : ''}</p>
                                                    </div>
                                                </div>
                                                <Badge className="bg-amber-100 text-amber-700 text-[10px]">Non verse</Badge>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}
